import type { CartSource } from './cartSource';

// Single source of truth for the cart item shape the backend expects when
// adding subscriptions, annual declarations, regularizations or
// ASAT/one-off products to a shopping cart.
//
// Why centralize this: four call sites used to assemble this object inline
// (ProductCard, RecommendedPlanCard, useRegularizationPurchase,
// useAnnualPurchase) and each one drifted slightly — different defaults for
// `source`, inconsistent handling of `regimes`, ad-hoc spreads for ally
// metadata. Funneling everything through this helper keeps the payload
// shape consistent and makes future schema changes a single edit.

export type CheckoutPayloadInput = {
	productVersionId: number;
	regimes?: string[];
	// OBLIGATORIO: qué superficie originó la compra. Era opcional con default
	// `window.location.pathname`, y ese default es justo lo que degradó el campo
	// a 131 valores (44% pathnames que no distinguen superficies dentro de una
	// misma ruta). Exigirlo hace que omitirlo no compile.
	source: CartSource;
	year?: number;
	month?: number;
	// Pass the Ally object (or anything with an `id`) — only the id ends up
	// in additional_information.ally.
	ally?: { id: string };
	// Free-form escape hatch merged into additional_information last, so it
	// can override anything else when needed.
	additionalInfo?: Record<string, unknown>;
	// Pins the price (in cents) the user actually saw, instead of letting the
	// backend resolve it from the catalog.
	//
	// The backend honors this ONLY when the product has `custom_price = true`
	// in the plans catalog — it is a per-product flag, not a product type. If
	// the flag is off it ignores the amount SILENTLY and charges list price
	// (no error, the item still lands in the cart), so never show a pinned
	// price in the UI without checking the flag for that product first.
	customProduct?: { total_paid: number };
};

export type CheckoutAdditionalInformation = {
	regimes: string[];
	source: CartSource;
	year?: number;
	month?: number;
	ally?: string;
	[key: string]: unknown;
};

export type CheckoutPayloadItem = {
	product_version_id: number;
	additional_information: CheckoutAdditionalInformation;
	custom_product?: { total_paid: number };
};

export const buildCheckoutPayload = (
	input: CheckoutPayloadInput,
): CheckoutPayloadItem => {
	const item: CheckoutPayloadItem = {
		product_version_id: input.productVersionId,
		additional_information: {
			regimes: input.regimes ?? [],
			source: input.source,
			...(input.year !== undefined ? { year: input.year } : {}),
			...(input.month !== undefined ? { month: input.month } : {}),
			...(input.ally ? { ally: input.ally.id } : {}),
			...(input.additionalInfo ?? {}),
		},
	};
	if (input.customProduct) {
		item.custom_product = input.customProduct;
	}
	return item;
};
